import { Chip, Tooltip } from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import WarningIcon from '@mui/icons-material/WarningAmber'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import dayjs from 'dayjs'

import { IGovernanceResponseEnvelope } from '../types'

interface IProps {
  envelope?: Pick<IGovernanceResponseEnvelope<unknown>, 'source_timestamp' | 'staleness_warning'>
  /**
   * Red threshold. If the envelope already ships `staleness_warning`, that
   * takes precedence; otherwise we flag `red` when age > redSeconds.
   * Default: 2h (consistent with Ops-0 §I.2.3 hard SLO).
   */
  redSeconds?: number
}

/**
 * FreshnessBadge (Ops-0 §I.2.3).
 *
 * Reads `source_timestamp` + `staleness_warning` from the governance envelope
 * and renders a colored chip (green OK / yellow soft-SLO / red hard-SLO).
 *
 * Colors mirror Ops-0: "data fresh" green, "data soft-stale" yellow,
 * "data hard-stale" red.
 */
export function FreshnessBadge({ envelope, redSeconds = 7200 }: IProps) {
  if (!envelope?.source_timestamp) return null
  const ts = dayjs(envelope.source_timestamp)
  const ageSec = Math.max(0, dayjs().diff(ts, 'second'))
  const warning = envelope.staleness_warning

  const relative = formatRelative(ageSec)
  const absolute = ts.format('DD/MM/YYYY HH:mm')

  let color: 'success' | 'warning' | 'error' = 'success'
  let Icon = AccessTimeIcon
  if (warning) {
    color = ageSec > redSeconds ? 'error' : 'warning'
    Icon = ageSec > redSeconds ? ReportProblemIcon : WarningIcon
  }

  return (
    <Tooltip title={`Fonte: ${absolute}${warning ? ` • SLA ${warning.threshold_seconds}s, idade ${warning.actual_age_seconds}s` : ''}`} arrow>
      <Chip
        size="small"
        color={color}
        variant="outlined"
        icon={<Icon fontSize="small" />}
        label={`Atualizado há ${relative}`}
      />
    </Tooltip>
  )
}

function formatRelative(sec: number): string {
  if (sec < 60) return `${sec}s`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}min`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h}h`
  const d = Math.floor(h / 24)
  return `${d}d`
}
